/**
 * Supply-price markup — CANONICAL pricing math (Phase B1)
 *
 * Single source of truth for the pure markup arithmetic shared by the
 * customer-facing DISPLAY pipeline (pricing-model-display.ts, which re-exports
 * `markUpPrice` from here) AND the payment engines (checkout.ts / cart.ts).
 * Keeping ONE implementation means the price a customer sees on a card, the
 * price validated at checkout, and the price the Edge webhook re-computes can
 * never drift. See admin/docs/SUPPLY_PRICE_MODEL_DESIGN_2026_07_03.md §1.2 / §4.
 *
 * ⚠️ MIRROR TWIN — this module MUST stay byte-for-byte equivalent in behavior
 * to the Edge helper
 *   locarora-admin/supabase/functions/_shared/pricing-model.ts
 * The web checkout/cart price and the Edge authoritative re-price MUST produce
 * identical numbers (the webhook amount-equality check depends on it). Any edit
 * to `applyMarkupToUnit` / `computeSiteFee` here must be applied there too.
 *
 * Formula (×1.15 transition, 2026-08):
 *   unitCustomer = roundMarkup( unitSupply × (1 + markupRate) )
 * The markup is a SURCHARGE on the supply price, not a margin carved out of the
 * customer price — a 15% rate means the customer pays 15% more than the supply
 * value (the earlier ÷(1−r) form charged ~17.6% more for the same rate).
 *
 * Rounding (`roundMarkupForCurrency`):
 *   - 0-decimal currencies (KRW/JPY/TWD) → nearest 5 units. A ×1.15 image of the
 *     integers hits only ~87% of integers, so 1-unit rounding leaves ~13% of
 *     display prices unreachable and makes round partner targets (e.g. ₩100,000)
 *     impossible to land on. Snapping to 5 gives partners a reachable grid.
 *   - 2-decimal currencies (USD/CNY/EUR) → unchanged `roundForCurrency` (0.01).
 *
 * NO server-only imports — safe from both server actions and client components.
 */

import {
  CURRENCY_INFO,
  roundForCurrency,
  type SupportedCurrency,
} from "@/lib/utils/currency";

/**
 * Rounding used for MARKUP results only (not site fee, not FX).
 * 0-decimal currencies snap to the nearest 5 units; everything else keeps the
 * currency's normal precision via {@link roundForCurrency}.
 */
function roundMarkupForCurrency(
  amount: number,
  currency: SupportedCurrency,
): number {
  const decimals = CURRENCY_INFO[currency]?.decimals ?? 2;
  if (decimals === 0) return Math.round(amount / 5) * 5;
  return roundForCurrency(amount, currency);
}

/**
 * Mark up a single SUPPLY unit price to the customer-facing price.
 * `rate` is the partner markup rate in the open interval (0, 1) (e.g. 0.15).
 * A rate outside that interval is treated as "no markup" so a bad/absent rate
 * can never divide-by-zero or unexpectedly inflate a price — the raw supply
 * value passes through unchanged.
 *
 * Applied at the UNIT level (rental unit price, each option price_adjustment,
 * each choice price) so that running the SAME totaling logic on supply units
 * and marked-up units yields `markupAmount = markedUpTotal − supplyTotal` by
 * subtraction (spec §4-3, architecture rule 4).
 *
 * Result = round-to-5 (0-decimal currencies) of `unitSupply × (1 + rate)`.
 */
export function applyMarkupToUnit(
  unitSupply: number,
  rate: number,
  currency: SupportedCurrency,
): number {
  if (!Number.isFinite(rate) || rate <= 0 || rate >= 1) return unitSupply;
  return roundMarkupForCurrency(unitSupply * (1 + rate), currency);
}

/**
 * Null-tolerant wrapper over {@link applyMarkupToUnit} used by the DISPLAY
 * pipeline (product cards, recently-viewed, wishlist). Preserves the historical
 * B2 signature so `pricing-model-display.ts` can re-export this unchanged.
 */
export function markUpPrice(
  supplyAmount: number | null | undefined,
  rate: number,
  currency: SupportedCurrency,
): number | null {
  if (supplyAmount == null) return supplyAmount ?? null;
  return applyMarkupToUnit(supplyAmount, rate, currency);
}

/**
 * Site fee for the markup model — a flat percentage of the (marked-up) subtotal
 * computed DIRECTLY in the product currency (no KRW round-trip / tier lookup;
 * spec §4-4). `feeRate` is a fraction (e.g. 0.02 for 2%) from `get_site_fee_rate`.
 * Rounds to the currency's decimals via `roundForCurrency` (KRW/JPY → integer).
 * Returns 0 for a non-positive subtotal or a non-positive/invalid rate.
 */
export function computeSiteFee(
  subtotal: number,
  feeRate: number,
  currency: SupportedCurrency,
): number {
  if (!Number.isFinite(feeRate) || feeRate <= 0 || subtotal <= 0) return 0;
  return roundForCurrency(subtotal * feeRate, currency);
}
